import { Box } from '@material-ui/core';
import { BorrowerDocumentationRequired, Loan } from '@nemuru-eng/js-api-client';

import { useCancelCardApplication } from '@/components/modules/DocumentsValidation/components/_shared/ActionsButtons/hooks/useCancelCardApplication';
import { CloseConfirmationAlert } from '@/components/modules/DocumentsValidation/components/_shared/CloseConfirmationAlert';
import { useDocumentsValidationContext } from '@/components/modules/DocumentsValidation/contexts/DocumentsValidationContext';
import { useValidationClose } from '@/components/modules/DocumentsValidation/hooks/useValidationClose';
import { ActionDialog } from '@/components/shared/components/ActionDialog';
import { Icon } from '@/components/shared/components/Icon';
import { MoreActionsMenuWithIcon } from '@/components/shared/components/MoreActionsMenu/MoreActionsMenuWithIcon';
import { ActionsOption } from '@/components/shared/components/MoreActionsMenu/types/ActionsOption';
import { StandardButton } from '@/components/shared/components/StandardButton';
import { Text } from '@/components/shared/components/Text';
import { useScreen } from '@/hooks/useScreen';

interface ActionsButtonsProps {
  revalidateValues: () => Promise<{ loan: Loan; documentation: BorrowerDocumentationRequired }>;
  resetValidationProcess: () => void;
}

export const ActionsButtons = ({ revalidateValues, resetValidationProcess }: ActionsButtonsProps) => {
  const { lg } = useScreen();
  const { update } = useDocumentsValidationContext();
  const { onCancel, opened, setOpened, toggleDialog, loading } = useCancelCardApplication({ revalidateValues });
  const { showCloseAlert, setShowCloseAlert, toggleCloseAlert, onClose, onLeave } = useValidationClose();

  const revalidateValuesAndUpdateContext = async () => {
    const { loan: revalidatedLoan, documentation: revalidatedDocumentation } = await revalidateValues();
    update({ loan: revalidatedLoan, borrowerDocumentationRequired: revalidatedDocumentation });
  };

  const handleOnClickCancelCardApplication = () => {
    setOpened(true);
  };

  const onResetValidation = async () => {
    await revalidateValuesAndUpdateContext();
    resetValidationProcess();
    onLeave();
  };

  const actionsOptionsArray: ActionsOption[] = [
    {
      display: <Text tId={'RESET_VALIDATION'} />,
      onClick: () => onClose(),
      iconName: 'refresh',
    },
    {
      display: <Text tId={'CANCEL_LOAN_BTN'} />,
      onClick: () => setOpened(true),
      iconName: 'close',
    },
  ];

  return (
    <Box position={'absolute'} top={'-80px'} right={0}>
      {!lg ? (
        <MoreActionsMenuWithIcon actionsOptionsArray={actionsOptionsArray} />
      ) : (
        <Box display="flex" flexDirection={lg ? 'row' : 'column'} gridGap={8}>
          <StandardButton size="small" variant="outlined" endIcon={<Icon size={16}>refresh</Icon>} onClick={onClose}>
            <Text tId={'RESET_VALIDATION'} />
          </StandardButton>
          <StandardButton
            size="small"
            variant="outlined"
            endIcon={<Icon size={16}>close</Icon>}
            onClick={handleOnClickCancelCardApplication}
          >
            <Text tId={'CANCEL_LOAN_BTN'} />
          </StandardButton>
        </Box>
      )}
      <CloseConfirmationAlert
        opened={showCloseAlert}
        setOpened={setShowCloseAlert}
        toggleDialog={toggleCloseAlert}
        onLeave={onResetValidation}
      />
      <ActionDialog
        opened={opened}
        setOpened={setOpened}
        handleClose={toggleDialog}
        loading={loading}
        Title={<Text tId={'CANCEL_LOAN'} />}
        onSubmit={onCancel}
        Description={<Text tId={'CANCEL_CARD_DESCRIPTION'} />}
        MainButton={<Text tId={'CANCEL_LOAN_BTN'} />}
      />
    </Box>
  );
};
